import React from 'react';
import { connect } from 'react-redux';
import {
  Box, Typography, Button, Chip, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip,
  Tabs, Tab, TextField, Switch, FormControlLabel, Accordion, AccordionSummary,
  AccordionDetails, Divider, MenuItem,
} from '@mui/material';
import AceEditor from 'react-ace';
import 'ace-builds/webpack-resolver';
import 'ace-builds/src-noconflict/mode-yaml';
import 'ace-builds/src-noconflict/theme-monokai';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { getPartnerGuid, hasAuthourity } from '../../lib/access.es6';
import {
  getEngineConfigsRequest,
  getEngineConfigRawRequest,
  getEngineConfigRawReset,
  publishEngineConfigRequest,
  publishEngineConfigReset,
  createEngineConfigRequest,
  createEngineConfigReset,
} from './actions.es6';

var TH = {
  fontSize: 11, fontWeight: 700, color: '#6B7280',
  textTransform: 'uppercase', letterSpacing: '0.05em',
  bgcolor: '#F9FAFB', borderBottom: '1px solid #E5E7EB',
  py: 1.25, px: 2,
};
var TD = {
  fontSize: 13, color: '#374151',
  borderBottom: '1px solid #F3F4F6',
  py: 1.5, px: 2,
};

function newEngineConfigTemplate(partner) {
  return [
    `partners:`,
    `  - ${partner}`,
    `process: kyc_collection`,
    `identifier_type: national_id`,
    `enabled: true`,
    ``,
    `inputs:`,
    `  - name: identifier`,
    `    required: true`,
    ``,
    `auth:`,
    `  type: bearer_custom`,
    `  token_url: "{{env.AUTH_URL}}/oauth/token"`,
    `  token_method: POST`,
    `  token_path: access_token`,
    `  cache_key: vendor_token`,
    `  cache_ttl: 840`,
    ``,
    `steps:`,
    `  - id: verify`,
    `    method: POST`,
    `    url: "{{env.BASE_URL}}/v1/verify"`,
    `    headers:`,
    `      Authorization: "Bearer {{auth.token}}"`,
    `      Content-Type: application/json`,
    `    body:`,
    `      id: "{{request.identifier}}"`,
    `    extract:`,
    `      outcome: outcome`,
    ``,
    `output:`,
    `  outcome: "{{steps.verify.outcome}}"`,
  ].join('\n');
}

function parseYaml(yamlStr) {
  try {
    // js-yaml is in the dep tree via webpack tooling
    var jsYaml = require('js-yaml');
    var load = jsYaml.load || jsYaml.safeLoad;
    return load(yamlStr) || {};
  } catch (e) {
    return null;
  }
}

function dumpYaml(obj) {
  try {
    var jsYaml = require('js-yaml');
    var dump = jsYaml.dump || jsYaml.safeDump;
    return dump(obj, { lineWidth: 120, noRefs: true });
  } catch (e) {
    return '';
  }
}

var SECTION_LABEL = { fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' };

function renderKVSection(label, data, onAdd, onUpdateKey, onUpdateVal, onRemove) {
  var entries = Object.entries(data || {});
  return (
    <Box sx={{ mt: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography sx={SECTION_LABEL}>{label}</Typography>
        <Button size="small" onClick={onAdd} sx={{ py: 0, minWidth: 'auto', fontSize: 11 }} startIcon={<AddIcon sx={{ fontSize: 14 }} />}>Add</Button>
      </Box>
      {entries.length > 0 && (
        <TableContainer sx={{ border: '1px solid #F3F4F6', borderRadius: 1 }}>
          <Table size="small">
            <TableBody>
              {entries.map(([k, v]) => (
                <TableRow key={k}>
                  <TableCell sx={{ py: 0.25, px: 1, width: '38%' }}>
                    <TextField size="small" value={k} placeholder="key" variant="standard" fullWidth
                      onChange={e => onUpdateKey(k, e.target.value)}
                      inputProps={{ style: { fontFamily: 'monospace', fontSize: 11 } }} />
                  </TableCell>
                  <TableCell sx={{ py: 0.25, px: 1 }}>
                    <TextField size="small" variant="standard" fullWidth
                      value={typeof v === 'string' ? v : JSON.stringify(v)}
                      placeholder="value or {{template}}"
                      onChange={e => onUpdateVal(k, e.target.value)}
                      inputProps={{ style: { fontFamily: 'monospace', fontSize: 11 } }} />
                  </TableCell>
                  <TableCell sx={{ py: 0.25, px: 0.5, width: 32 }}>
                    <IconButton size="small" onClick={() => onRemove(k)} sx={{ color: '#D1D5DB' }}>
                      <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

var LOCKED_INPUTS = [
  { name: 'identifier', required: true },
  { name: 'identifier_type', required: true },
];
var LOCKED_INPUT_NAMES = new Set(['identifier', 'identifier_type']);

function getEditableInputs(parsed) {
  var all = parsed.inputs || [];
  return all.filter(function(f) { return !LOCKED_INPUT_NAMES.has(f.name); });
}

function setInputs(parsed, editableInputs) {
  parsed.inputs = [
    ...LOCKED_INPUTS,
    ...editableInputs.filter(function(f) { return !LOCKED_INPUT_NAMES.has(f.name); }),
  ];
}

function extractRequestFields(parsed) {
  const fields = new Set();
  const re = /\{\{request\.([a-zA-Z0-9_]+)\}\}/g;
  function scan(val) {
    if (typeof val === 'string') {
      let m;
      re.lastIndex = 0;
      while ((m = re.exec(val)) !== null) fields.add(m[1]);
    } else if (val && typeof val === 'object') {
      Object.values(val).forEach(scan);
    }
  }
  (parsed.steps || []).forEach(step => {
    scan(step.url);
    scan(step.headers);
    scan(step.body);
    scan(step.query);
  });
  return Array.from(fields).sort();
}

function applyFormChanges(currentYaml, updater) {
  var parsed = parseYaml(currentYaml);
  if (!parsed) return currentYaml;
  updater(parsed);
  return dumpYaml(parsed);
}

class EngineConfigs extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      editorOpen: false,
      isNew: false,
      selectedConfig: null,
      editorContent: '',
      hasYamlErrors: false,
      activeTab: 'ui',
      newPartnerInput: '',
    };
  }

  componentWillMount() {
    this.props.dispatch(getEngineConfigsRequest(getPartnerGuid()));
  }

  componentDidUpdate(prevProps) {
    const { engine_config_raw, successfully_published, successfully_created } = this.props;
    if (engine_config_raw && engine_config_raw !== prevProps.engine_config_raw) {
      this.setState({ editorContent: engine_config_raw });
    }
    if (successfully_published && successfully_published !== prevProps.successfully_published) {
      this.closeEditor();
    }
    if (successfully_created && successfully_created !== prevProps.successfully_created) {
      this.closeEditor();
    }
  }

  openEdit(config) {
    this.props.dispatch(getEngineConfigRawReset());
    this.props.dispatch(publishEngineConfigReset());
    this.setState({
      editorOpen: true, isNew: false, selectedConfig: config,
      editorContent: '', activeTab: 'ui', newPartnerInput: '',
    });
    this.props.dispatch(getEngineConfigRawRequest(config.partner, config.process, config.identifier_type));
  }

  openCreate() {
    this.props.dispatch(getEngineConfigRawReset());
    this.props.dispatch(createEngineConfigReset());
    this.setState({
      editorOpen: true, isNew: true, selectedConfig: null, activeTab: 'ui', newPartnerInput: '',
      editorContent: newEngineConfigTemplate(getPartnerGuid()),
    });
  }

  closeEditor() {
    this.setState({ editorOpen: false, selectedConfig: null, editorContent: '', activeTab: 'ui' });
    this.props.dispatch(getEngineConfigRawReset());
    this.props.dispatch(publishEngineConfigReset());
    this.props.dispatch(createEngineConfigReset());
  }

  handlePublish() {
    const { editorContent, isNew, selectedConfig } = this.state;
    const partner = getPartnerGuid();
    if (isNew) {
      this.props.dispatch(createEngineConfigRequest(partner, editorContent));
    } else {
      this.props.dispatch(publishEngineConfigRequest(
        selectedConfig.partner, selectedConfig.process, selectedConfig.identifier_type, editorContent,
      ));
    }
  }

  // UI tab form change helpers — each patches the YAML in-place
  setFormField(path, value) {
    const newYaml = applyFormChanges(this.state.editorContent, parsed => {
      const keys = path.split('.');
      let obj = parsed;
      for (var i = 0; i < keys.length - 1; i++) {
        if (!obj[keys[i]]) obj[keys[i]] = {};
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
    });
    this.setState({ editorContent: newYaml });
  }

  addPartner(slug) {
    if (!slug || !slug.trim()) return;
    const newYaml = applyFormChanges(this.state.editorContent, parsed => {
      if (!parsed.partners) parsed.partners = parsed.partner ? [parsed.partner] : [];
      if (!parsed.partners.includes(slug.trim())) parsed.partners.push(slug.trim());
      delete parsed.partner;
    });
    this.setState({ editorContent: newYaml, newPartnerInput: '' });
  }

  removePartner(slug) {
    const newYaml = applyFormChanges(this.state.editorContent, parsed => {
      const slugs = parsed.partners || (parsed.partner ? [parsed.partner] : []);
      const updated = slugs.filter(s => s !== slug);
      parsed.partners = updated;
      delete parsed.partner;
    });
    this.setState({ editorContent: newYaml });
  }

  addInputField() {
    const newYaml = applyFormChanges(this.state.editorContent, parsed => {
      const editable = getEditableInputs(parsed);
      setInputs(parsed, [...editable, { name: '', required: false }]);
    });
    this.setState({ editorContent: newYaml });
  }

  updateInputFieldName(idx, value) {
    const newYaml = applyFormChanges(this.state.editorContent, parsed => {
      const editable = getEditableInputs(parsed);
      editable[idx] = { ...editable[idx], name: value };
      setInputs(parsed, editable);
    });
    this.setState({ editorContent: newYaml });
  }

  updateInputFieldRequired(idx, value) {
    const newYaml = applyFormChanges(this.state.editorContent, parsed => {
      const editable = getEditableInputs(parsed);
      editable[idx] = { ...editable[idx], required: value };
      setInputs(parsed, editable);
    });
    this.setState({ editorContent: newYaml });
  }

  removeInputField(idx) {
    const newYaml = applyFormChanges(this.state.editorContent, parsed => {
      const editable = getEditableInputs(parsed);
      setInputs(parsed, editable.filter((_, i) => i !== idx));
    });
    this.setState({ editorContent: newYaml });
  }

  addOutputMapping() {
    const newYaml = applyFormChanges(this.state.editorContent, parsed => {
      if (!parsed.output) parsed.output = {};
      parsed.output[''] = '';
    });
    this.setState({ editorContent: newYaml });
  }

  updateOutputKey(oldKey, newKey) {
    const newYaml = applyFormChanges(this.state.editorContent, parsed => {
      if (!parsed.output) return;
      const val = parsed.output[oldKey];
      delete parsed.output[oldKey];
      parsed.output[newKey] = val || '';
    });
    this.setState({ editorContent: newYaml });
  }

  updateOutputValue(key, value) {
    const newYaml = applyFormChanges(this.state.editorContent, parsed => {
      if (!parsed.output) parsed.output = {};
      parsed.output[key] = value;
    });
    this.setState({ editorContent: newYaml });
  }

  removeOutputMapping(key) {
    const newYaml = applyFormChanges(this.state.editorContent, parsed => {
      if (parsed.output) delete parsed.output[key];
    });
    this.setState({ editorContent: newYaml });
  }

  addStep() {
    const newYaml = applyFormChanges(this.state.editorContent, parsed => {
      if (!parsed.steps) parsed.steps = [];
      const n = parsed.steps.length + 1;
      parsed.steps.push({ id: `step${n}`, method: 'POST', url: '', headers: {}, body: {}, extract: {} });
    });
    this.setState({ editorContent: newYaml });
  }

  removeStep(idx) {
    const newYaml = applyFormChanges(this.state.editorContent, parsed => {
      parsed.steps.splice(idx, 1);
    });
    this.setState({ editorContent: newYaml });
  }

  updateStep(idx, fn) {
    const newYaml = applyFormChanges(this.state.editorContent, parsed => {
      fn(parsed.steps[idx]);
    });
    this.setState({ editorContent: newYaml });
  }

  renderUITab(parsed) {
    if (!parsed) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="warning">YAML has errors — fix them in the YAML tab first.</Alert>
        </Box>
      );
    }

    const partners = parsed.partners || (parsed.partner ? [parsed.partner] : []);
    const { newPartnerInput } = this.state;

    return (
      <Box sx={{ p: 2, overflowY: 'auto', height: '100%' }}>

        {/* Basic Info */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#374151' }}>Basic Info</Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Process"
            size="small"
            value={parsed.process || ''}
            onChange={e => this.setFormField('process', e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <TextField
            label="Identifier Type"
            size="small"
            value={parsed.identifier_type || ''}
            onChange={e => this.setFormField('identifier_type', e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={parsed.enabled !== false}
                onChange={e => this.setFormField('enabled', e.target.checked)}
                color="success"
              />
            }
            label="Enabled"
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Partners */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#374151' }}>Partners</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
          {partners.map(slug => (
            <Chip
              key={slug}
              label={slug}
              size="small"
              onDelete={() => this.removePartner(slug)}
              sx={{ fontFamily: 'monospace', fontSize: 11 }}
            />
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            size="small"
            placeholder="partner_slug"
            value={newPartnerInput}
            onChange={e => this.setState({ newPartnerInput: e.target.value })}
            onKeyDown={e => { if (e.key === 'Enter') this.addPartner(newPartnerInput); }}
            sx={{ minWidth: 200, fontFamily: 'monospace' }}
            inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => this.addPartner(newPartnerInput)}
          >
            Add
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Auth */}
        {parsed.auth && (
          <Accordion defaultExpanded disableGutters sx={{ mb: 2, border: '1px solid #E5E7EB', borderRadius: 1, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#374151' }}>Auth</Typography>
              <Chip label={parsed.auth.type || 'bearer_custom'} size="small" sx={{ ml: 1, fontSize: 10 }} />
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Token URL"
                  size="small"
                  fullWidth
                  value={parsed.auth.token_url || ''}
                  onChange={e => this.setFormField('auth.token_url', e.target.value)}
                  inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
                />
                <TextField
                  label="Token Path"
                  size="small"
                  value={parsed.auth.token_path || ''}
                  onChange={e => this.setFormField('auth.token_path', e.target.value)}
                  sx={{ minWidth: 160 }}
                  inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
                />
                <TextField
                  label="Cache Key"
                  size="small"
                  value={parsed.auth.cache_key || ''}
                  onChange={e => this.setFormField('auth.cache_key', e.target.value)}
                  sx={{ minWidth: 200 }}
                  inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
                />
                <TextField
                  label="Cache TTL (s)"
                  size="small"
                  type="number"
                  value={parsed.auth.cache_ttl || 3600}
                  onChange={e => this.setFormField('auth.cache_ttl', parseInt(e.target.value, 10) || 3600)}
                  sx={{ minWidth: 120 }}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Steps */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#374151' }}>
            Steps ({(parsed.steps || []).length})
          </Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={() => this.addStep()}>Add Step</Button>
        </Box>
        {(!parsed.steps || parsed.steps.length === 0) && (
          <Box sx={{ textAlign: 'center', py: 2, color: '#9CA3AF', fontSize: 12, border: '1px dashed #E5E7EB', borderRadius: 1, mb: 1 }}>
            No steps defined. Click "Add Step" to create one.
          </Box>
        )}
        {(parsed.steps || []).map((step, idx) => (
          <Accordion key={idx} disableGutters sx={{ mb: 1, border: '1px solid #E5E7EB', borderRadius: 1, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, mr: 0 } }}>
              <Chip label={step.method || 'POST'} size="small" color="primary" sx={{ fontSize: 10, minWidth: 52 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', flex: 1 }}>{step.id || `step${idx + 1}`}</Typography>
              {step.poll && <Chip label="polling" size="small" color="warning" sx={{ fontSize: 10, mr: 1 }} />}
              <IconButton size="small" onClick={e => { e.stopPropagation(); this.removeStep(idx); }} sx={{ color: '#D1D5DB', mr: 0.5 }}>
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 1 }}>
              <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                <TextField
                  label="Step ID"
                  size="small"
                  value={step.id || ''}
                  onChange={e => this.updateStep(idx, s => { s.id = e.target.value; })}
                  sx={{ minWidth: 160 }}
                  inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
                />
                <TextField
                  select
                  label="Method"
                  size="small"
                  value={step.method || 'POST'}
                  onChange={e => this.updateStep(idx, s => { s.method = e.target.value; })}
                  sx={{ minWidth: 120 }}
                >
                  {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map(m => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </TextField>
              </Box>
              <TextField
                label="URL"
                size="small"
                fullWidth
                value={step.url || ''}
                onChange={e => this.updateStep(idx, s => { s.url = e.target.value; })}
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
                sx={{ mb: 1 }}
              />
              {renderKVSection(
                'Headers',
                step.headers,
                () => this.updateStep(idx, s => { if (!s.headers) s.headers = {}; s.headers['new_header'] = ''; }),
                (oldKey, newKey) => this.updateStep(idx, s => { const v = s.headers[oldKey]; delete s.headers[oldKey]; s.headers[newKey] = v || ''; }),
                (key, val) => this.updateStep(idx, s => { s.headers[key] = val; }),
                (key) => this.updateStep(idx, s => { delete s.headers[key]; }),
              )}
              {['POST', 'PUT', 'PATCH'].includes(step.method) && renderKVSection(
                'Body',
                step.body,
                () => this.updateStep(idx, s => { if (!s.body) s.body = {}; s.body[''] = ''; }),
                (oldKey, newKey) => this.updateStep(idx, s => { const v = s.body[oldKey]; delete s.body[oldKey]; s.body[newKey] = v || ''; }),
                (key, val) => this.updateStep(idx, s => { s.body[key] = val; }),
                (key) => this.updateStep(idx, s => { delete s.body[key]; }),
              )}
              {renderKVSection(
                'Query Params',
                step.query,
                () => this.updateStep(idx, s => { if (!s.query) s.query = {}; s.query[''] = ''; }),
                (oldKey, newKey) => this.updateStep(idx, s => { const v = s.query[oldKey]; delete s.query[oldKey]; s.query[newKey] = v || ''; }),
                (key, val) => this.updateStep(idx, s => { s.query[key] = val; }),
                (key) => this.updateStep(idx, s => { delete s.query[key]; }),
              )}
              {renderKVSection(
                'Extract',
                step.extract,
                () => this.updateStep(idx, s => { if (!s.extract) s.extract = {}; s.extract[''] = ''; }),
                (oldKey, newKey) => this.updateStep(idx, s => { const v = s.extract[oldKey]; delete s.extract[oldKey]; s.extract[newKey] = v || ''; }),
                (key, val) => this.updateStep(idx, s => { s.extract[key] = val; }),
                (key) => this.updateStep(idx, s => { delete s.extract[key]; }),
              )}
              {/* Assert */}
              <Box sx={{ mt: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography sx={SECTION_LABEL}>Assertions</Typography>
                  <Button size="small" onClick={() => this.updateStep(idx, s => {
                    if (!s.assert) s.assert = [];
                    s.assert.push({ path: '', equals: '', error_path: '' });
                  })} sx={{ py: 0, minWidth: 'auto', fontSize: 11 }} startIcon={<AddIcon sx={{ fontSize: 14 }} />}>Add</Button>
                </Box>
                {(step.assert || []).length > 0 && (
                  <TableContainer sx={{ border: '1px solid #F3F4F6', borderRadius: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', py: 0.75, px: 1 }}>Path</TableCell>
                          <TableCell sx={{ fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', py: 0.75, px: 1 }}>Equals</TableCell>
                          <TableCell sx={{ fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', py: 0.75, px: 1 }}>Error Path</TableCell>
                          <TableCell sx={{ py: 0.75, px: 0.5, width: 32 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(step.assert || []).map((assertion, aIdx) => (
                          <TableRow key={aIdx}>
                            <TableCell sx={{ py: 0.25, px: 1 }}>
                              <TextField size="small" variant="standard" fullWidth value={assertion.path || ''}
                                placeholder="status"
                                onChange={e => this.updateStep(idx, s => { s.assert[aIdx] = { ...s.assert[aIdx], path: e.target.value }; })}
                                inputProps={{ style: { fontFamily: 'monospace', fontSize: 11 } }} />
                            </TableCell>
                            <TableCell sx={{ py: 0.25, px: 1 }}>
                              <TextField size="small" variant="standard" fullWidth value={assertion.equals || ''}
                                placeholder="success"
                                onChange={e => this.updateStep(idx, s => { s.assert[aIdx] = { ...s.assert[aIdx], equals: e.target.value }; })}
                                inputProps={{ style: { fontFamily: 'monospace', fontSize: 11 } }} />
                            </TableCell>
                            <TableCell sx={{ py: 0.25, px: 1 }}>
                              <TextField size="small" variant="standard" fullWidth value={assertion.error_path || ''}
                                placeholder="error"
                                onChange={e => this.updateStep(idx, s => { s.assert[aIdx] = { ...s.assert[aIdx], error_path: e.target.value }; })}
                                inputProps={{ style: { fontFamily: 'monospace', fontSize: 11 } }} />
                            </TableCell>
                            <TableCell sx={{ py: 0.25, px: 0.5 }}>
                              <IconButton size="small" onClick={() => this.updateStep(idx, s => { s.assert.splice(aIdx, 1); })} sx={{ color: '#D1D5DB' }}>
                                <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
              <Box sx={{ mt: 1.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!step.poll}
                      size="small"
                      onChange={e => this.updateStep(idx, s => {
                        if (e.target.checked) { s.poll = { until: '', interval_ms: 3000, max_attempts: 10 }; }
                        else { delete s.poll; }
                      })}
                    />
                  }
                  label={<Typography sx={SECTION_LABEL}>Polling</Typography>}
                />
                {step.poll && (
                  <Box sx={{ display: 'flex', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
                    <TextField
                      label="Until (expression)"
                      size="small"
                      value={step.poll.until || ''}
                      onChange={e => this.updateStep(idx, s => { s.poll.until = e.target.value; })}
                      sx={{ minWidth: 260 }}
                      inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
                      placeholder="{{steps.poll.status}} == 'COMPLETED'"
                    />
                    <TextField
                      label="Interval (ms)"
                      size="small"
                      type="number"
                      value={step.poll.interval_ms || 3000}
                      onChange={e => this.updateStep(idx, s => { s.poll.interval_ms = parseInt(e.target.value, 10) || 3000; })}
                      sx={{ minWidth: 130 }}
                    />
                    <TextField
                      label="Max Attempts"
                      size="small"
                      type="number"
                      value={step.poll.max_attempts || 10}
                      onChange={e => this.updateStep(idx, s => { s.poll.max_attempts = parseInt(e.target.value, 10) || 10; })}
                      sx={{ minWidth: 130 }}
                    />
                  </Box>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}

        <Divider sx={{ my: 2 }} />

        {/* Input Parameters */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#374151' }}>Input Parameters</Typography>
            <Typography variant="caption" sx={{ color: '#6B7280' }}>
              Fields passed by the caller as request params (used via <code style={{ fontFamily: 'monospace' }}>{'{{request.*}}'}</code> in steps)
            </Typography>
          </Box>
          <Button size="small" startIcon={<AddIcon />} onClick={() => this.addInputField()}>Add</Button>
        </Box>
        <TableContainer sx={{ border: '1px solid #E5E7EB', borderRadius: 1, mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', py: 1, px: 1.5 }}>Field Name</TableCell>
                <TableCell sx={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', py: 1, px: 1.5 }}>Required</TableCell>
                <TableCell sx={{ py: 1, px: 1 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {LOCKED_INPUTS.map(field => (
                <TableRow key={field.name} sx={{ bgcolor: '#F9FAFB' }}>
                  <TableCell sx={{ py: 0.5, px: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <SettingsOutlinedIcon sx={{ fontSize: 13, color: '#9CA3AF' }} />
                      <Typography sx={{ fontFamily: 'monospace', fontSize: 12, color: '#6B7280' }}>{field.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 0.5, px: 1.5, width: 90 }}>
                    <Switch size="small" checked disabled color="warning" />
                  </TableCell>
                  <TableCell sx={{ py: 0.5, px: 1, width: 40 }} />
                </TableRow>
              ))}
              {(() => {
                const editable = getEditableInputs(parsed);
                return editable.length === 0 ? null : editable.map((field, idx) => (
                  <TableRow key={idx}>
                    <TableCell sx={{ py: 0.5, px: 1.5 }}>
                      <TextField
                        size="small"
                        value={field.name || ''}
                        placeholder="field_name"
                        onChange={e => this.updateInputFieldName(idx, e.target.value)}
                        inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
                        variant="standard"
                        fullWidth
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1.5, width: 90 }}>
                      <Switch
                        size="small"
                        checked={field.required === true}
                        onChange={e => this.updateInputFieldRequired(idx, e.target.checked)}
                        color="warning"
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1, width: 40 }}>
                      <IconButton size="small" onClick={() => this.removeInputField(idx)} sx={{ color: '#9CA3AF' }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ));
              })()}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ mb: 2 }} />

        {/* Output Mappings */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#374151' }}>
            Output Mappings
          </Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={() => this.addOutputMapping()}>
            Add
          </Button>
        </Box>
        <TableContainer sx={{ border: '1px solid #E5E7EB', borderRadius: 1, mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', py: 1, px: 1.5 }}>Key</TableCell>
                <TableCell sx={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', py: 1, px: 1.5 }}>Template Expression</TableCell>
                <TableCell sx={{ py: 1, px: 1 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.keys(parsed.output || {}).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} sx={{ textAlign: 'center', color: '#9CA3AF', fontSize: 12, py: 2 }}>
                    No output mappings defined.
                  </TableCell>
                </TableRow>
              ) : Object.entries(parsed.output || {}).map(([key, value]) => (
                <TableRow key={key}>
                  <TableCell sx={{ py: 0.5, px: 1.5, width: '35%' }}>
                    <TextField
                      size="small"
                      value={key}
                      placeholder="outcome"
                      onChange={e => this.updateOutputKey(key, e.target.value)}
                      inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
                      variant="standard"
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.5, px: 1.5 }}>
                    <TextField
                      size="small"
                      value={value || ''}
                      placeholder="{{steps.poll.outcome}}"
                      onChange={e => this.updateOutputValue(key, e.target.value)}
                      inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
                      variant="standard"
                      fullWidth
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.5, px: 1 }}>
                    <IconButton size="small" onClick={() => this.removeOutputMapping(key)} sx={{ color: '#9CA3AF' }}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  render() {
    if (!hasAuthourity('VIEW_UNDERWRITING_FLOWS')) return null;

    const {
      loader, engine_configs, get_configs_error,
      config_raw_loader, get_config_raw_error,
      publish_loader, publish_error,
      create_loader, create_error,
    } = this.props;
    const { editorOpen, isNew, selectedConfig, editorContent, hasYamlErrors, activeTab } = this.state;

    const saving = publish_loader || create_loader;
    const saveError = publish_error || create_error;
    const parsed = activeTab === 'ui' ? parseYaml(editorContent) : null;

    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsOutlinedIcon sx={{ color: '#6B7280' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827' }}>
              KYC Configs
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => this.openCreate()}
            sx={{ bgcolor: 'primary.main', textTransform: 'none' }}
          >
            New Config
          </Button>
        </Box>

        {get_configs_error && (
          <Alert severity="error" sx={{ mb: 2 }}>Failed to load KYC configs.</Alert>
        )}

        {loader ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ border: '1px solid #E5E7EB', borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={TH}>Partner</TableCell>
                  <TableCell sx={TH}>Process</TableCell>
                  <TableCell sx={TH}>Identifier Type</TableCell>
                  <TableCell sx={TH}>Status</TableCell>
                  <TableCell sx={TH} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!engine_configs || engine_configs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ ...TD, textAlign: 'center', color: '#9CA3AF', py: 4 }}>
                      No KYC configs found.
                    </TableCell>
                  </TableRow>
                ) : (
                  engine_configs.map(config => (
                    <TableRow key={`${config.partner}:${config.process}:${config.identifier_type}`} hover>
                      <TableCell sx={{ ...TD, fontFamily: 'monospace', fontSize: 12 }}>{config.partner}</TableCell>
                      <TableCell sx={{ ...TD }}>
                        <Chip label={config.process} size="small" color="primary" variant="outlined" sx={{ fontSize: 11 }} />
                      </TableCell>
                      <TableCell sx={{ ...TD, fontFamily: 'monospace', fontSize: 12 }}>{config.identifier_type}</TableCell>
                      <TableCell sx={TD}>
                        <Chip
                          label={config.enabled !== false ? 'enabled' : 'disabled'}
                          size="small"
                          color={config.enabled !== false ? 'success' : 'default'}
                          sx={{ fontSize: 11 }}
                        />
                      </TableCell>
                      <TableCell sx={TD} align="right">
                        <Tooltip title="Edit Config">
                          <IconButton size="small" onClick={() => this.openEdit(config)} sx={{ color: '#0D47A1' }}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog
          open={editorOpen}
          onClose={() => this.closeEditor()}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { height: '90vh', display: 'flex', flexDirection: 'column' } }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 0 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {isNew ? 'New KYC Config' : (selectedConfig && `${selectedConfig.partner} — ${selectedConfig.process} / ${selectedConfig.identifier_type}`)}
              </Typography>
              {!isNew && selectedConfig && (
                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6B7280' }}>
                  {selectedConfig.partner}:{selectedConfig.process}:{selectedConfig.identifier_type}
                </Typography>
              )}
            </Box>
            <IconButton size="small" onClick={() => this.closeEditor()}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <Tabs
            value={activeTab}
            onChange={(_, v) => this.setState({ activeTab: v })}
            sx={{ px: 2, borderBottom: '1px solid #E5E7EB' }}
          >
            <Tab value="ui" label="UI" sx={{ textTransform: 'none', fontSize: 13 }} />
            <Tab value="yaml" label="YAML" sx={{ textTransform: 'none', fontSize: 13, fontFamily: 'monospace' }} />
          </Tabs>

          <DialogContent sx={{ p: 0, flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {saveError && (
              <Alert severity="error" sx={{ mx: 2, mt: 1 }}>
                {(saveError && saveError.message) || 'Failed to save config. Check your YAML is valid.'}
              </Alert>
            )}
            {get_config_raw_error && (
              <Alert severity="error" sx={{ mx: 2, mt: 1 }}>Failed to load config content.</Alert>
            )}

            {config_raw_loader ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                <CircularProgress />
              </Box>
            ) : activeTab === 'yaml' ? (
              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <AceEditor
                  mode="yaml"
                  theme="monokai"
                  value={editorContent}
                  onChange={value => this.setState({ editorContent: value })}
                  onValidate={annotations => this.setState({ hasYamlErrors: annotations.some(a => a.type === 'error') })}
                  name="engine-config-yaml-editor"
                  width="100%"
                  height="100%"
                  fontSize={13}
                  showPrintMargin={false}
                  showGutter={true}
                  highlightActiveLine={true}
                  editorProps={{ $blockScrolling: true }}
                  setOptions={{
                    enableBasicAutocompletion: true,
                    enableLiveAutocompletion: false,
                    showLineNumbers: true,
                    tabSize: 2,
                    useWorker: true,
                  }}
                />
              </Box>
            ) : (
              <Box sx={{ flex: 1, overflowY: 'auto' }}>
                {this.renderUITab(parsed)}
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #E5E7EB' }}>
            <Button onClick={() => this.closeEditor()} disabled={saving}>Cancel</Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CloudUploadOutlinedIcon />}
              onClick={() => this.handlePublish()}
              disabled={saving || config_raw_loader || !editorContent || (activeTab === 'yaml' && hasYamlErrors)}
              sx={{ textTransform: 'none' }}
            >
              {saving ? 'Publishing…' : 'Publish'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}

function mapStateToProps(state) {
  return {
    loader: state.engineConfigs.loader,
    engine_configs: state.engineConfigs.engine_configs,
    get_configs_error: state.engineConfigs.get_configs_error,

    config_raw_loader: state.engineConfigs.config_raw_loader,
    engine_config_raw: state.engineConfigs.engine_config_raw,
    get_config_raw_error: state.engineConfigs.get_config_raw_error,

    publish_loader: state.engineConfigs.publish_loader,
    successfully_published: state.engineConfigs.successfully_published,
    publish_error: state.engineConfigs.publish_error,

    create_loader: state.engineConfigs.create_loader,
    successfully_created: state.engineConfigs.successfully_created,
    create_error: state.engineConfigs.create_error,
  };
}

export default connect(mapStateToProps)(EngineConfigs);
